import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  InputGroup,
  InputRightAddon,
  NumberInput,
  NumberInputField,
  Text,
  VStack,
} from '@efishery/onefish';
import { ArrowSquareDown } from '@onefish/icons-react';
import {
  Address,
  Card,
  ComodityDescription,
  Description,
  GeoPopover,
} from 'components';
import { comodity, generateCta } from 'features/transport/utils';
import { useGeoLocation } from 'hooks';
import { geoSavePermission } from 'hooks/useGeoLocation/helper';
import { FormEvent } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { OrderDetail } from 'types';
import { getAttachmentLink } from 'utils/order';
import { formatShipDate } from '../../../helpers';
import { composeDeliveryMessage } from '../../helpers';
import { UploadSuratJalanField } from './UploadSuratJalanField';
import {
  isSuratJalanInvalid,
  suratJalanErrMsg,
  useDeliveryLoadingForm,
} from './forms';
import { useDeliveryLoading } from './hooks';

type DeliveryLoadingProps = {
  deliveryDetail: OrderDetail;
  isEnableGeoLocation?: boolean;
};

export const DeliveryLoading = ({
  deliveryDetail,
  isEnableGeoLocation,
}: DeliveryLoadingProps) => {
  const {
    register,
    handleSubmit,
    trigger,
    errors,
    methods,
    watchSuratJalanInput,
  } = useDeliveryLoadingForm(deliveryDetail);

  const suratJalan = getAttachmentLink(
    deliveryDetail.attachments,
    'CONSIGNMENT_NOTE',
  );

  const {
    updateToNextStatus,
    isLoading,
    isSubmitDisabled,
    confirmationButtonHandler,
  } = useDeliveryLoading(deliveryDetail, watchSuratJalanInput, suratJalan);

  const pageName = 'delivery-loading';
  const {
    isShouldShowPopover,
    onTogglePopover,
    onClosePopover,
    isLocationActive,
    ...geoProps
  } = useGeoLocation({
    positionKey: deliveryDetail.id,
    pageName,
  });

  const _prependFormWithGeo = () => {
    navigator.geolocation.getCurrentPosition(position => {
      geoSavePermission('granted', pageName);
      register('latitude', { value: position.coords.latitude });
      register('longitude', { value: position.coords.longitude });
      handleSubmit(updateToNextStatus)();
    });
  };

  const submitHandlerWithGeo = (e: FormEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (!isEnableGeoLocation) {
      handleSubmit(updateToNextStatus)();
      return;
    }

    if (isShouldShowPopover()) {
      onTogglePopover();
      return;
    }

    if (document && navigator && isLocationActive()) {
      _prependFormWithGeo();
      return;
    }

    handleSubmit(updateToNextStatus)();
  };

  return (
    <>
      <FormProvider {...methods}>
        <VStack
          w="full"
          alignItems="stretch"
          spacing="3"
          mb="6"
          as="form"
          onSubmit={submitHandlerWithGeo}
        >
          <Card p="4">
            <VStack w="full" alignItems="stretch" spacing="3">
              <Heading as="h2" size="md">
                Muat Barang
              </Heading>

              <Box bg="brand.70" borderRadius="md" p="3" color="brand.600">
                <Box>
                  <Text fontWeight="semibold">
                    {deliveryDetail.origin_name}
                  </Text>
                  <HStack>
                    <ArrowSquareDown weight="fill" />
                    <Text fontSize="sm">Muat barang</Text>
                  </HStack>
                </Box>
              </Box>

              <Text fontSize="md" lineHeight="5" color="grey.500">
                {composeDeliveryMessage(deliveryDetail)}
              </Text>
            </VStack>
          </Card>

          <Address
            address={deliveryDetail.dest_address}
            city={deliveryDetail.dest_city_name}
            province={deliveryDetail.dest_province_name}
          />

          <Card p="4">
            <Description
              name="Tanggal Penjemputan"
              value={formatShipDate(deliveryDetail.delivery_at)}
            />
          </Card>

          {deliveryDetail.order_lines.map((orderLine, index) => {
            return (
              <Card p="4" key={orderLine.id}>
                <VStack w="full" alignItems="stretch" spacing="2">
                  <ComodityDescription
                    name="Jenis"
                    businessType={deliveryDetail?.business_type}
                    value={orderLine.product_name}
                  />
                  <ComodityDescription
                    name="Ukuran"
                    businessType={deliveryDetail?.business_type}
                    value={orderLine.product_name}
                  />

                  <Divider variant="dashed" />

                  <Description
                    name="Total Tonase yang Akan Dibawa"
                    value={orderLine.quantity}
                    format="thousand-separator"
                    unit="kg"
                  />

                  <Divider variant="dashed" />

                  <FormControl
                    isInvalid={Boolean(
                      errors?.order_lines?.[index]?.carried_quantity,
                    )}
                  >
                    <FormLabel fontSize="sm">
                      Total {comodity(deliveryDetail?.business_type)} Dibawa
                    </FormLabel>
                    <InputGroup>
                      <NumberInput w="full" precision={2}>
                        <NumberInputField
                          borderEndRadius="none"
                          data-testid={`input_total_dibawa_${index + 1}`}
                          {...register(
                            `order_lines.${index}.carried_quantity`,
                            {
                              required: 'Tidak boleh kosong!',
                              valueAsNumber: true,
                              max: {
                                value: orderLine.quantity,
                                message: 'Melebihi jumlah yang diorder',
                              },
                              onBlur: () => {
                                trigger(
                                  `order_lines.${index}.carried_quantity`,
                                );
                              },
                            },
                          )}
                        />
                      </NumberInput>
                      <InputRightAddon children="kg" />
                    </InputGroup>
                    <FormErrorMessage>
                      {errors.order_lines?.[index]?.carried_quantity?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <Description
                    name="Total Harga"
                    value={new Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                      minimumFractionDigits: 2,
                    }).format(orderLine.original_price)}
                  />
                </VStack>
              </Card>
            );
          })}

          {suratJalan ? (
            <>
              <Card p="4">
                <Button
                  size={'md'}
                  variant="solid"
                  width={'full'}
                  onClick={confirmationButtonHandler}
                >
                  Konfirmasi Total {comodity(deliveryDetail?.business_type)}{' '}
                  yang dibawa
                </Button>
              </Card>
              <Card p="4">
                <Link hrefLang="id" to={suratJalan} target="_blank">
                  <object
                    data={suratJalan}
                    type="application/pdf"
                    width="100%"
                  />
                  <Button size={'md'} variant="link" width={'full'}>
                    Surat Jalan
                  </Button>
                </Link>
              </Card>
            </>
          ) : (
            <Card p="4">
              <UploadSuratJalanField
                name="attachments.0.link"
                isInvalid={isSuratJalanInvalid(errors)}
                message={suratJalanErrMsg(errors)}
              />
            </Card>
          )}

          <Card p="4">
            <Description
              name="Grand Total"
              value={new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 2,
              }).format(deliveryDetail.total_original_price_nett)}
            />
          </Card>

          <Button
            width="full"
            type="submit"
            isLoading={isLoading}
            loadingText="Memperbarui status..."
            isDisabled={isSubmitDisabled}
            data-testid="btn_submit_loading"
          >
            {generateCta(deliveryDetail.status)}
          </Button>
        </VStack>
      </FormProvider>
      {isEnableGeoLocation && (
        <GeoPopover
          {...geoProps}
          yesHandler={() => {
            _prependFormWithGeo();
            onClosePopover();
          }}
        />
      )}
    </>
  );
};
